<template>
  <!-- The Business Model Section -->
  <div class="content-section animate fadeInUp">
    <h2 class="text-center">The Business Model</h2>

    <p class="animate haveLineHeight">
      Like a living universe that expands according to the consciousness
      connected to it, the brand's identity, business logic, and strategic
      blueprint evolve through time<br /><span
        class="bigText inlineBlock serif emphasis animate fadeInLeft text-center"
        >~ from the <span class="biggerText textColour">past</span>,</span
      ><br /><span
        class="bigText fullWidth inlineBlock serif emphasis animate fadeInDown text-center largestMarginTopBottom"
      >
        through the <span class="biggerText textColour">present</span>, </span
      ><br /><span
        class="bigText fullWidth inlineBlock serif emphasis animate fadeInRight text-right largeLineHeight"
      >
        and into the emerging
        <span class="biggerText textColour">future</span> ~</span
      ><br />the temporal flow inviting the audience into a collective
      initiatory experience where fiction, reality, and the subconscious merge,
      allowing the impossible to take form.
    </p>
    <p class="animate">
      Storytelling remains the engine of all marketing and strategic growth.
      Personal connection with the characters cultivates a devoted audience
      whose loyalty transcends traditional engagement and becomes participatory.
      The business strategy of The Path is designed to amplify this devotion,
      positioning each spectator not merely as a viewer but as an observer of
      the characters’ intimate, stylised world - and ultimately, a participant
      within it.
    </p>
    <blockquote class="animate fadeInLeft textColour text-right">
      The signature style of The Path is to give form to its fictionalised
      universes.
      <strong class="textColourWhite"
        >Marketing and business strategies evolve as organically as the stories
        themselves</strong
      >, ensuring that every touchpoint remains authentic to the truth of each
      narrative.
    </blockquote>
    <p class="animate fadeInDown">
      The decade-long plan unfolds through a structured narrative supported by
      seven interconnected business streams. Each stream functions as a pillar
      of monetisation, a driver of world-building, and a gateway through which
      the audience enters the universe.
    </p>

    <p
      class="text-center smallFontSize largestMarginTopBottom business-grid-intro"
    >
      Click on each segment below to explore the business streams. This section
      is restricted to investor candidates. Request access if you are an aligned
      prospect.
    </p>

    <!-- Business Segments Grid (2 columns on desktop, 1 on mobile) -->
    <div class="image-containerRow">
      <!-- Segment 1: The Film Projects -->
      <div
        class="image-wrapper column"
        @click="openDrawer('film')"
        aria-label="Open details for The Stories & Executive Production"
      >
        <h3 class="animate active">The Stories & Executive Production</h3>
        <img
          :src="filmImage"
          alt="The Stories & Executive Production"
          class="characterImg animate zoomIn"
          loading="lazy"
        />
        <p class="subtitle">
          The Path serves as Executive Producer of all twelve cinematic works,
          in partnership with a primary studio or aligned production house. Each
          project is part of a unified authorship structure that expands across
          film, literature, performance, and immersive media - forming the
          backbone of the universe's creative and financial architecture.
        </p>
      </div>

      <!-- Segment 2: The Characters -->
      <div
        class="image-wrapper column"
        @click="openDrawer('characters')"
        aria-label="Open details for The Characters & Immersive Marketing"
      >
        <h3 class="animate active">The Characters & Immersive Marketing</h3>
        <img
          :src="charactersImage"
          alt="The Characters & Immersive Marketing"
          class="characterImg animate zoomIn"
          loading="lazy"
        />
        <p class="subtitle">
          The brand's marketing force follows the evolution of its three
          embodied characters - Cecilia, Patricia, and The Path. Social presence
          becomes part of the storytelling itself, delivering symbols, clues,
          and narrative fragments that draw the audience deeper into the
          universe and activate real-time immersion.
        </p>
      </div>

      <!-- Segment 3: The Artefacts -->
      <div
        class="image-wrapper column"
        @click="openDrawer('artefacts')"
        aria-label="Open details for The Artefacts"
      >
        <h3 class="animate active">The Artefacts</h3>
        <img
          :src="artefactsImage"
          alt="The Artefacts"
          class="characterImg animate zoomIn"
          loading="lazy"
        />
        <p class="subtitle">
          Twelve collections of seven original artefacts will accompany the
          cinematic works. Appearing both on screen and in physical form, these
          objects act as tangible story extensions - allowing the audience to
          merge with the narrative materially while owning pieces of the
          universe's mythology.
        </p>
      </div>

      <!-- Segment 4: The App -->
      <div
        class="image-wrapper column"
        @click="openDrawer('app')"
        aria-label="Open details for The App"
      >
        <h3 class="animate active">The App</h3>
        <img
          :src="appImage"
          alt="The App"
          class="characterImg animate zoomIn"
          loading="lazy"
        />
        <p class="subtitle">
          The collective initiation unfolds inside The 33 App - an interactive
          portal that adapts to each user's intuitive path. Serving as the
          gateway between the visible and the unseen, the app transforms
          spectators into participants, guiding them through the metaphysical
          architecture of The Path's consciousness and the unique transmission
          within each story's universe.
        </p>
      </div>

      <!-- Segment 5: The Documentary -->
      <div
        class="image-wrapper column"
        @click="openDrawer('documentary')"
        aria-label="Open details for The Documentary"
      >
        <h3 class="animate active">The Documentary</h3>
        <img
          :src="documentaryImage"
          alt="The Documentary"
          class="characterImg animate zoomIn"
          loading="lazy"
        />
        <p class="subtitle">
          Filmed across the decade of creation, the documentary chronicles how
          consciousness, art, and technology converged to build this universe.
          It reveals the unseen architecture behind the stories - a reflection
          on myth-making, identity, and the birth of a new cinematic paradigm.
        </p>
      </div>

      <!-- Segment 6: Cultural & Institutional Partnerships -->
      <div
        class="image-wrapper column"
        @click="openDrawer('partnerships')"
        aria-label="Open details for Cultural & Institutional Partnerships"
      >
        <h3 class="animate active">Cultural & Institutional Partnerships</h3>
        <img
          :src="partnershipsImage"
          alt="Cultural & Institutional Partnerships"
          class="characterImg animate zoomIn"
          loading="lazy"
        />
        <p class="subtitle">
          Strategic collaborations with museums, cultural houses, institutions,
          and governmental bodies support both the films and the universe
          surrounding them. Each partnership expands into curated live
          experiences - installations, performances, and interactive events that
          echo the themes of each story. These experiences unlock symbols,
          codes, and narrative layers connected to The33App, allowing audiences
          worldwide (physically or digitally) to step inside the mythic
          architecture of each cinematic work.
        </p>
      </div>

      <!-- Segment 7: The Source Helix Creative System (centered) -->
      <div
        class="image-wrapper column centered"
        @click="openDrawer('helix')"
        aria-label="Open details for The Source Helix Creative System"
      >
        <h3 class="animate active">The Source Helix Creative System™</h3>
        <img
          :src="helixImage"
          alt="The Source Helix Creative System"
          class="characterImg animate zoomIn"
          loading="lazy"
        />
        <p class="subtitle">
          The Helix Creative System™ evolves into the seventh stream - a
          proprietary creative technology that eventually expands into
          masterclasses, initiations, creative training, and future licensing of
          its narrative methodology. This stream becomes fully active once the
          system reaches maturity during the later years of the first
          transmission and at the threshold of the 44 Transmission.
        </p>
      </div>
    </div>
  </div>

  <!-- Business Model Table -->
  <div class="content-section animate fadeInUp">
    <h2 class="text-center">Monetization Strategy</h2>

    <p class="table-caption-above text-center smallFontSize grey-dark">
      The values below represent indicative activation costs for the seed and
      early development phase. This table exists to give prospective investors
      an initial sense of scale before accessing deeper financial materials.
    </p>

    <!-- Seed Round Block -->
    <div class="seed-round-block">
      <h3 class="block-title">SEED ROUND / MILESTONE 1</h3>
      <SwissStyleTable
        :headers="['Category', 'Amount (€)', 'Purpose']"
        :compactMargin="true"
        :rows="[
          [
            'Seed Round Target',
            '€2,000,000',
            'The Seed Round finances the foundational ignition of The33Path - early development across all twelve stories, macro-IP protection, brand and character identity, the app\'s first prototype, artefact design and prototyping, company formation, early operational team, executive development structure, and the initial activation of The Helix Creative System™',
          ],
          ['Committed to Date', '€0', '—'],
          ['Remaining', '€2,000,000', '—'],
        ]"
        :emphasizeColumnIndex="0"
      />
    </div>

    <!-- Business Streams Preview Block -->
    <div class="business-streams-preview-block">
      <h3 class="block-title">
        INITIAL ACTIVATION OF BUSINESS STREAMS (PREVIEW)
      </h3>
      <SwissStyleTable
        :headers="[
          'Business Stream',
          'Indicative Range',
          'Description Preview',
        ]"
        :noWrapColumns="[1]"
        :compactMargin="true"
        :rows="[
          [
            '0. Foundational Operations & Infrastructure',
            '€451K - €690K',
            'Company formation, legal setup, contracts, accounting, insurance, treasury, software systems, operational costs, founder reimbursement (pre-incorporation work + website), first-six-month leadership salaries (Founder, COO, CFO, Exec Director, Exec Assistant) and initial contingency reserve',
          ],
          [
            '1. The Stories & Executive Production',
            '€600K - €780K',
            'All 12 film treatments and 3 series bibles, universe architecture, visual language, script frameworks, literary reviser (Novella 1), early executive production planning, and Cecilia/Patricia training (acting + vocal).',
          ],
          [
            '2. The Characters & Immersive Marketing',
            '€150K - €250K',
            'Brand identity, logo system, tone reels, early marketing visuals, pre-launch marketing strategies, development of advertisement videos, character embodiment system, and next-phase website development.',
          ],
          [
            '3. The Artefacts (6 per collection x 12)',
            '€120K - €180K',
            'Concept design, material research, prototypes, co-brand exploration, licensing groundwork, integration into story architecture.',
          ],
          [
            '4. The App (The 33 App)',
            '€180K - €260K',
            'UX mapping, portal logic, metaphysical decision-tree system, early prototype, internal interface design, dev-scoping, app–film–artefact integration blueprint.',
          ],
          [
            '5. The Documentary',
            '€40K - €70K',
            'Documentary bible, metaphysical sequence planning, first interviews, archival workflow, early observational footage, and 12-year filming structure.',
          ],
          [
            '6. Cultural & Institutional Partnerships',
            '€30K - €60K',
            'Partnership decks, museum/festival outreach, fashion/arts collaboration planning, and conceptual groundwork for The PATH Mythic Kingdom',
          ],
          [
            '7. The Helix Creative System™',
            '€60K - €100K',
            'Creative protocol blueprint, metaphysical workflow system, cross-team alignment structures, and talent licensing architecture.',
          ],
        ]"
        :emphasizeColumnIndex="0"
      />
    </div>

    <p class="table-caption-below text-center smallFontSize grey-dark">
      All financial values represent projected investments and may adjust
      through co-production partnerships. These indicative ranges express
      proportional early-stage activation costs per business stream and do not
      sum to the total Seed Round, as they illustrate intervals rather than
      closed budgets. Complete financial breakdowns, ROI cycles, and revenue
      structures are available exclusively within the Investor Overview,
      Financial Architecture, and Full Business Plan sections for Investor
      Candidates and Confirmed Investors.
    </p>
  </div>

  <!-- Document Access Section -->
  <!-- Role hierarchy: visitor < IC (Investor Candidate) < CI (Confirmed Investor) < admin -->
  <div class="content-section document-access-section animate fadeInUp">
    <!-- Investor Overview - Paragraph visible to all, button to IC+ -->
    <div class="document-section">
      <p>
        The Investor Overview offers a clear and
        <span class="textColour">high-level introduction</span> to The33Path for
        potential investors entering the universe for the first time. It
        outlines the project's vision, the 10-year expansion model, the
        seed-round structure, and the strategic advantages of joining the
        ecosystem at this early stage. Access is granted by request only;
        receiving it represents entry into a rare and culturally significant
        creative system.
      </p>
      <button @click="openDrawer('investorOverview')" class="document-button">
        Investor Overview
      </button>
      <p class="document-access-note italic smallFontSize grey-dark">
        <svg class="icon-inline lock-icon">
          <use href="@/assets/iconset.svg#lock_open"></use>
        </svg>
        Document accessible exclusively to Investor Candidates.
      </p>
    </div>

    <!-- Financial Architecture - Paragraph visible to all, button to CI+ -->
    <div class="document-section">
      <p>
        The Financial Architecture presents the full structural logic of
        The33Path - a
        <span class="textColour"
          >vertically integrated creative and financial ecosystem</span
        >
        designed for long-term expansion. It details budget models, compensation
        structures, revenue pathways, ROI logic, and how all business streams
        interconnect across the decade.
      </p>
      <button
        @click="openDrawer('financialArchitecture')"
        class="document-button"
      >
        Financial Architecture
      </button>
      <p class="document-access-note italic smallFontSize grey-dark">
        <svg class="icon-inline lock-icon">
          <use href="@/assets/iconset.svg#lock_closed"></use>
        </svg>
        Document accessible only to Confirmed Investors and collaborators under
        NDA.
      </p>
    </div>

    <!-- Full Business Plan - Paragraph visible to all, button to CI+ -->
    <div class="document-section">
      <p>
        The Full Business Plan reveals the
        <span class="textColour"
          >complete creative and operational architecture</span
        >
        of The33Path: its narrative system, brand identity, production strategy,
        multi-stream model, and long-term roadmap for global cultural presence.
        It outlines how each stream generates and compounds value throughout the
        decade.
      </p>
      <button @click="openDrawer('businessPlan')" class="document-button">
        Full Business Plan
      </button>
      <p class="document-access-note italic smallFontSize grey-dark">
        <svg class="icon-inline lock-icon">
          <use href="@/assets/iconset.svg#lock_closed"></use>
        </svg>
        Document accessible only to Confirmed Investors and collaborators under
        NDA.
      </p>
    </div>
  </div>

  <!-- Partnership Principles Section -->
  <div class="content-section partnership-principles-section">
    <h2 class="text-center">Partnership Principles</h2>
    <p class="subtitle text-center">Non-Negotiables</p>

    <p>
      The Path is an authored universe - a living work of art and consciousness.
      Every partnership, whether creative, financial, or institutional, must
      align with its founding principles: authorship integrity, philosophical
      coherence, and long-term vision.
    </p>

    <div class="principles-grid">
      <div class="principle-item">
        <h4>Authorship Sovereignty</h4>
        <p>
          The IP, narrative structure, and creative direction remain fully
          governed by The33Path™, under the sole authorship of Patricia Ribeiro.
          <span class="textColour">Core authorship cannot be diluted</span> or
          redirected.
        </p>
      </div>

      <div class="principle-item">
        <h4>Creative Control</h4>
        <p>
          Final artistic approval - including scripts, casting, direction,
          design, and marketing - rests with The33Path to ensure coherence of
          <span class="textColour">coherence of vision</span> and fidelity to
          the transmission from which the stories originate.
        </p>
      </div>

      <div class="principle-item">
        <h4>Equity Structure</h4>
        <p>
          Partners may participate at the project or collection level (20–40%),
          while The Path's macro-brand and transmedia IP remain
          <span class="textColour">non-dilutable</span>.
        </p>
      </div>

      <div class="principle-item">
        <h4>Ecosystem Alignment</h4>
        <p>
          All ancillary streams - including the App, Artefacts, Publishing, and
          Documentary - operate under the
          <span class="textColour"
            >governance of The Helix Creative System™</span
          >, even when co-financed.
        </p>
      </div>

      <div class="principle-item">
        <h4>Ethical & Energetic Integrity</h4>
        <p>
          <span class="textColour"
            >Collaboration is welcome; compromise is not</span
          >. Partnerships must align with the universe's aesthetic, ethical, and
          metaphysical foundation.
        </p>
      </div>
    </div>

    <p class="closing-statement text-center italic animate fadeInUp">
      The universe expands through many hands - but only when those hands
      protect its essence.
    </p>
  </div>

  <div class="why-now-divider borderTop"></div>

  <!-- Why Now Section -->
  <div class="content-section why-now-section">
    <h2 class="text-center animate fadeInSubtleUp">Why Now</h2>
    <p class="subtitle text-center animate fadeInSubtleUp">
      The Window of Opportunity
    </p>

    <blockquote class="why-now-master animate fadeInSubtleUp">
      The33Path emerges at
      <span class="textColour">a decisive cultural moment</span> where film,
      technology, and consciousness are converging into new narrative
      ecosystems.
    </blockquote>

    <p class="animate fadeInSubtleUp">
      Audiences are moving away from passive entertainment and toward immersive
      worlds, symbolic meaning, and embodied creative experiences. The33Path
      stands at the forefront of this shift - not as a film franchise, but as a
      living, multi-stream ecosystem engineered for the new paradigm.
    </p>

    <p class="animate fadeInSubtleUp">
      To step in now is to enter at the moment when the universe is being
      forged: the stories, the characters, the brand identity, the artefacts,
      the app, and the decade-long experiential blueprint. This stage carries a
      <span class="textColour"
        >a rare form of strategic advantage and prestige</span
      >. Sovereignty is intact; no external attachments exist by design. Early
      partners help shape the model rather than adapt to it. Investors enter
      before acceleration, before valuation increases, and before the ecosystem
      opens to the world.
    </p>

    <p class="animate fadeInSubtleUp">
      The33Path generates a form of
      <span class="textColour">experiential resonance</span> unlike anything in
      contemporary entertainment - a blend of clarity, emotional activation,
      symbolic recognition, and mythic intimacy that invites audiences not
      merely to watch, but to enter and awaken. Engagement is not dependent on
      box-office cycles; it is sustained by artefact collections, app missions,
      immersive storytelling, and cultural activations that form a continuous,
      self-renewing revenue ecosystem.
    </p>

    <p class="animate fadeInSubtleUp">
      This project does not fit the industry's existing mold. It is building a
      new one - a model where narrative, consciousness, design, technology, and
      lived mythology operate as a single engine.
      <span class="textColour"
        >No traditional studio system can replicate this</span
      >
      coherence of tone, symbolism, and frequency, because it originates from a
      proprietary creative architecture and a unified authorial source. A
      partner studio benefits not only from cinematic success, but from the
      uplift of an entire brand with its own gravity and long-tail cultural
      impact.
    </p>

    <p class="animate fadeInSubtleUp">
      For those aligned with this vision, the timing is unequivocal. Those who
      join at this stage become custodians of a once-in-a-generation cultural
      undertaking: a transmedia universe designed to expand consciousness,
      redefine cinematic storytelling, and set a new precedent for how story,
      art, technology, and human experience merge into one living ecosystem.
      Entering now is not simply an investment - it is alignment with a new
      paradigm, the inevitable forging of a new world.
    </p>
  </div>

  <!-- BottomDrawer usage -->
  <BottomDrawer :isOpen="activeDrawer !== null" @closeDrawer="closeDrawer">
    <template v-if="activeDrawer === 'film' && canAccessFilmProjects">
      <!-- New Business Stream Content Component for Stream 1 -->
      <BusinessStreamContent
        :streamNumber="businessStreamsData.stream1.streamNumber"
        :streamName="businessStreamsData.stream1.streamName"
        :streamSubtitle="businessStreamsData.stream1.streamSubtitle"
        :streamTagline="businessStreamsData.stream1.streamTagline"
        :streamTaglineLine1="businessStreamsData.stream1.streamTaglineLine1"
        :streamTaglineLine2="businessStreamsData.stream1.streamTaglineLine2"
        :introText="businessStreamsData.stream1.introText"
        :introBulletPoints="businessStreamsData.stream1.introBulletPoints"
        :introClosing="businessStreamsData.stream1.introClosing"
        :narrativeSections="businessStreamsData.stream1.narrativeSections"
        :strategicContent="businessStreamsData.stream1.strategicContent"
        :closingSection="businessStreamsData.stream1.closingSection"
        :nextStreamLabel="businessStreamsData.stream1.nextStreamLabel"
        :nextStreamId="businessStreamsData.stream1.nextStreamId"
        :nextStreamNumber="businessStreamsData.stream1.nextStreamNumber"
        @navigateToStream="openDrawer"
      >
        <!-- Roadmap slot content -->
        <template #roadmap>
          <FilmProjectsRoadmapPath />
        </template>
      </BusinessStreamContent>
    </template>

    <template v-else-if="activeDrawer === 'film' && !canAccessFilmProjects">
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">The Film Projects</h3>

        <div class="restricted-drawer-content-simple">
          <p v-if="!isFilmProjectsEnabled" class="restricted-message">
            Coming soon.
          </p>
          <p v-else class="restricted-message">
            {{ getRestrictionMessage("theFilmProjects") }}
          </p>

          <p
            v-if="userRole === 'visitor' && isInvestorCandidateVisibility('theFilmProjects')"
            class="smallFontSize grey-dark"
            style="margin-top: var(--space)"
          >
            Access to Investor Candidate materials is subject to a brief confidentiality agreement due to the sensitive nature of the information.
          </p>

          <!-- For Visitors: Request Access button -->
          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="
              handleContentRequest('theFilmProjects', 'The Film Projects')
            "
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <!-- For VIPs: only show "Notify me" if content is disabled -->
          <button
            v-if="userRole === 'vip' && !isFilmProjectsEnabled"
            class="notify-button"
            @click="
              handleContentRequest('theFilmProjects', 'The Film Projects')
            "
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="activeDrawer === 'characters' && canAccessCharacters">
      <div class="characters-content-section">
        <BusinessStreamContent
          :streamNumber="businessStreamsData.stream2.streamNumber"
          :streamName="businessStreamsData.stream2.streamName"
          :streamSubtitle="businessStreamsData.stream2.streamSubtitle"
          :streamTagline="businessStreamsData.stream2.streamTagline"
          :streamTaglineLine1="businessStreamsData.stream2.streamTaglineLine1"
          :streamTaglineLine2="businessStreamsData.stream2.streamTaglineLine2"
          :introText="businessStreamsData.stream2.introText"
          :narrativeSections="businessStreamsData.stream2.narrativeSections"
          :strategicContent="businessStreamsData.stream2.strategicContent"
          :closingSection="businessStreamsData.stream2.closingSection"
          :nextStreamLabel="businessStreamsData.stream2.nextStreamLabel"
          :nextStreamId="businessStreamsData.stream2.nextStreamId"
          :nextStreamNumber="businessStreamsData.stream2.nextStreamNumber"
          @navigateToStream="openDrawer"
        />
      </div>
    </template>

    <template v-else-if="activeDrawer === 'characters' && !canAccessCharacters">
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">The Characters</h3>
        <div class="restricted-drawer-content-simple">
          <p v-if="!isCharactersEnabled" class="restricted-message">
            Coming soon.
          </p>
          <p v-else class="restricted-message">
            {{ getRestrictionMessage("theCharacters") }}
          </p>

          <!-- For Visitors: Request Access button -->
          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="handleContentRequest('theCharacters', 'The Characters')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <!-- For VIPs: only show "Notify me" if content is disabled -->
          <button
            v-if="userRole === 'vip' && !isCharactersEnabled"
            class="notify-button"
            @click="handleContentRequest('theCharacters', 'The Characters')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="activeDrawer === 'artefacts' && !canAccessArtefacts">
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">The Artefacts</h3>
        <div class="restricted-drawer-content-simple">
          <p v-if="!isArtefactsEnabled" class="restricted-message">
            Coming soon.
          </p>
          <p v-else class="restricted-message">
            {{ getRestrictionMessage("theArtefacts") }}
          </p>

          <p
            v-if="userRole === 'visitor' && isInvestorCandidateVisibility('theArtefacts')"
            class="smallFontSize grey-dark"
            style="margin-top: var(--space)"
          >
            Access to Investor Candidate materials is subject to a brief confidentiality agreement due to the sensitive nature of the information.
          </p>

          <!-- For Visitors: Request Access button -->
          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="handleContentRequest('theArtefacts', 'The Artefacts')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <!-- For VIPs: only show "Notify me" if content is disabled -->
          <button
            v-if="userRole === 'vip' && !isArtefactsEnabled"
            class="notify-button"
            @click="handleContentRequest('theArtefacts', 'The Artefacts')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="activeDrawer === 'app' && !canAccessApp">
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">The App</h3>
        <div class="restricted-drawer-content-simple">
          <p v-if="!isAppEnabled" class="restricted-message">Coming soon.</p>
          <p v-else class="restricted-message">
            {{ getRestrictionMessage("theApp") }}
          </p>

          <p
            v-if="userRole === 'visitor' && isInvestorCandidateVisibility('theApp')"
            class="smallFontSize grey-dark"
            style="margin-top: var(--space)"
          >
            Access to Investor Candidate materials is subject to a brief confidentiality agreement due to the sensitive nature of the information.
          </p>

          <!-- For Visitors: Request Access button -->
          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="handleContentRequest('theApp', 'The App')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <!-- For VIPs: only show "Notify me" if content is disabled -->
          <button
            v-if="userRole === 'vip' && !isAppEnabled"
            class="notify-button"
            @click="handleContentRequest('theApp', 'The App')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="activeDrawer === 'artefacts' && canAccessArtefacts">
      <div class="contentsContArtefacts">
        <DynamicBackgroundWatcher />
        <BusinessStreamContent
          :streamNumber="businessStreamsData.stream3.streamNumber"
          :streamName="businessStreamsData.stream3.streamName"
          :streamSubtitle="businessStreamsData.stream3.streamSubtitle"
          :streamTagline="businessStreamsData.stream3.streamTagline"
          :streamTaglineLine1="businessStreamsData.stream3.streamTaglineLine1"
          :streamTaglineLine2="businessStreamsData.stream3.streamTaglineLine2"
          :introText="businessStreamsData.stream3.introText"
          :narrativeSections="businessStreamsData.stream3.narrativeSections"
          :strategicContent="businessStreamsData.stream3.strategicContent"
          :closingSection="businessStreamsData.stream3.closingSection"
          :nextStreamLabel="businessStreamsData.stream3.nextStreamLabel"
          :nextStreamId="businessStreamsData.stream3.nextStreamId"
          :nextStreamNumber="businessStreamsData.stream3.nextStreamNumber"
          @navigateToStream="openDrawer"
        />
      </div>
    </template>

    <template v-else-if="activeDrawer === 'app' && canAccessApp">
      <div class="app-content-section">
        <BusinessStreamContent
          :streamNumber="businessStreamsData.stream4.streamNumber"
          :streamName="businessStreamsData.stream4.streamName"
          :streamSubtitle="businessStreamsData.stream4.streamSubtitle"
          :streamTagline="businessStreamsData.stream4.streamTagline"
          :streamTaglineLine1="businessStreamsData.stream4.streamTaglineLine1"
          :streamTaglineLine2="businessStreamsData.stream4.streamTaglineLine2"
          :introText="businessStreamsData.stream4.introText"
          :narrativeSections="businessStreamsData.stream4.narrativeSections"
          :strategicContent="businessStreamsData.stream4.strategicContent"
          :closingSection="businessStreamsData.stream4.closingSection"
          :nextStreamLabel="businessStreamsData.stream4.nextStreamLabel"
          :nextStreamId="businessStreamsData.stream4.nextStreamId"
          :nextStreamNumber="businessStreamsData.stream4.nextStreamNumber"
          @navigateToStream="openDrawer"
        />
      </div>
    </template>

    <!-- Documentary Drawer Templates -->
    <template
      v-else-if="activeDrawer === 'documentary' && canAccessDocumentary"
    >
      <div class="documentary-content-section">
        <BusinessStreamContent
          :streamNumber="businessStreamsData.stream5.streamNumber"
          :streamName="businessStreamsData.stream5.streamName"
          :streamSubtitle="businessStreamsData.stream5.streamSubtitle"
          :streamTagline="businessStreamsData.stream5.streamTagline"
          :streamTaglineLine1="businessStreamsData.stream5.streamTaglineLine1"
          :streamTaglineLine2="businessStreamsData.stream5.streamTaglineLine2"
          :introText="businessStreamsData.stream5.introText"
          :narrativeSections="businessStreamsData.stream5.narrativeSections"
          :strategicContent="businessStreamsData.stream5.strategicContent"
          :closingSection="businessStreamsData.stream5.closingSection"
          :nextStreamLabel="businessStreamsData.stream5.nextStreamLabel"
          :nextStreamId="businessStreamsData.stream5.nextStreamId"
          :nextStreamNumber="businessStreamsData.stream5.nextStreamNumber"
          @navigateToStream="openDrawer"
        />
      </div>
    </template>

    <template
      v-else-if="activeDrawer === 'documentary' && !canAccessDocumentary"
    >
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">The Documentary</h3>
        <div class="restricted-drawer-content-simple">
          <p v-if="!isDocumentaryEnabled" class="restricted-message">
            Coming soon.
          </p>
          <p v-else class="restricted-message">
            {{ getRestrictionMessage("theDocumentary") }}
          </p>

          <p
            v-if="userRole === 'visitor' && isInvestorCandidateVisibility('theDocumentary')"
            class="smallFontSize grey-dark"
            style="margin-top: var(--space)"
          >
            Access to Investor Candidate materials is subject to a brief confidentiality agreement due to the sensitive nature of the information.
          </p>

          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="handleContentRequest('theDocumentary', 'The Documentary')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <button
            v-if="userRole === 'vip' && !isDocumentaryEnabled"
            class="notify-button"
            @click="handleContentRequest('theDocumentary', 'The Documentary')"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>

    <!-- Partnerships Drawer Templates -->
    <template
      v-else-if="activeDrawer === 'partnerships' && canAccessPartnerships"
    >
      <div class="partnerships-content-section">
        <BusinessStreamContent
          :streamNumber="businessStreamsData.stream6.streamNumber"
          :streamName="businessStreamsData.stream6.streamName"
          :streamSubtitle="businessStreamsData.stream6.streamSubtitle"
          :streamTagline="businessStreamsData.stream6.streamTagline"
          :streamTaglineLine1="businessStreamsData.stream6.streamTaglineLine1"
          :streamTaglineLine2="businessStreamsData.stream6.streamTaglineLine2"
          :introText="businessStreamsData.stream6.introText"
          :introImages="businessStreamsData.stream6.introImages"
          :narrativeSections="businessStreamsData.stream6.narrativeSections"
          :strategicContent="businessStreamsData.stream6.strategicContent"
          :closingSection="businessStreamsData.stream6.closingSection"
          :nextStreamLabel="businessStreamsData.stream6.nextStreamLabel"
          :nextStreamId="businessStreamsData.stream6.nextStreamId"
          :nextStreamNumber="businessStreamsData.stream6.nextStreamNumber"
          @navigateToStream="openDrawer"
        />
      </div>
    </template>

    <template
      v-else-if="activeDrawer === 'partnerships' && !canAccessPartnerships"
    >
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">
          Cultural & Institutional Partnerships
        </h3>
        <div class="restricted-drawer-content-simple">
          <p v-if="!isPartnershipsEnabled" class="restricted-message">
            Coming soon.
          </p>
          <p v-else class="restricted-message">
            {{ getRestrictionMessage("thePartnerships") }}
          </p>

          <p
            v-if="userRole === 'visitor' && isInvestorCandidateVisibility('thePartnerships')"
            class="smallFontSize grey-dark"
            style="margin-top: var(--space)"
          >
            Access to Investor Candidate materials is subject to a brief confidentiality agreement due to the sensitive nature of the information.
          </p>

          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="
              handleContentRequest(
                'thePartnerships',
                'Cultural & Institutional Partnerships',
              )
            "
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <button
            v-if="userRole === 'vip' && !isPartnershipsEnabled"
            class="notify-button"
            @click="
              handleContentRequest(
                'thePartnerships',
                'Cultural & Institutional Partnerships',
              )
            "
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>

    <!-- Helix Drawer Templates -->
    <template v-else-if="activeDrawer === 'helix' && canAccessHelix">
      <div class="helix-content-section">
        <BusinessStreamContent
          :streamNumber="businessStreamsData.stream7.streamNumber"
          :streamName="businessStreamsData.stream7.streamName"
          :streamSubtitle="businessStreamsData.stream7.streamSubtitle"
          :streamTagline="businessStreamsData.stream7.streamTagline"
          :streamTaglineLine1="businessStreamsData.stream7.streamTaglineLine1"
          :streamTaglineLine2="businessStreamsData.stream7.streamTaglineLine2"
          :introText="businessStreamsData.stream7.introText"
          :introBulletPoints="businessStreamsData.stream7.introBulletPoints"
          :introClosing="businessStreamsData.stream7.introClosing"
          :introPhaseBullets="businessStreamsData.stream7.introPhaseBullets"
          :introFinalParagraph="businessStreamsData.stream7.introFinalParagraph"
          :narrativeSections="businessStreamsData.stream7.narrativeSections"
          :strategicContent="businessStreamsData.stream7.strategicContent"
          :closingSection="businessStreamsData.stream7.closingSection"
          :closingAccessNote="businessStreamsData.stream7.closingAccessNote"
          @navigateToStream="openDrawer"
        />
      </div>
    </template>

    <template v-else-if="activeDrawer === 'helix' && !canAccessHelix">
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">
          The Source Helix Creative System™
        </h3>
        <div class="restricted-drawer-content-simple">
          <p v-if="!isHelixEnabled" class="restricted-message">Coming soon.</p>
          <p v-else class="restricted-message">
            {{ getRestrictionMessage("theHelix") }}
          </p>

          <button
            v-if="userRole === 'visitor'"
            class="request-access-button"
            @click="
              handleContentRequest(
                'theHelix',
                'The Source Helix Creative System',
              )
            "
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Request Access</span>
          </button>

          <button
            v-if="userRole === 'vip' && !isHelixEnabled"
            class="notify-button"
            @click="
              handleContentRequest(
                'theHelix',
                'The Source Helix Creative System',
              )
            "
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">Processing...</span>
            <span v-else>Notify me when available</span>
          </button>
        </div>
      </div>
    </template>

    <!-- Investor Overview Drawer -->
    <template v-else-if="activeDrawer === 'investorOverview' && canAccessInvestorOverview">
      <InvestorOverviewContent :data="investorOverviewData" />
    </template>

    <template v-else-if="activeDrawer === 'investorOverview' && !canAccessInvestorOverview">
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">Investor Overview</h3>
        <div class="restricted-drawer-content-simple">
          <p class="restricted-message">
            This document is only available for candidate investors.
          </p>
          <p class="smallFontSize grey-dark" style="margin-top: var(--space)">
            The Investor Overview offers a clear and high-level introduction to
            The33Path for potential investors entering the universe for the
            first time. It outlines the project's vision, the 10-year expansion
            model, the seed-round structure, and the strategic advantages of
            joining the ecosystem at this early stage.
          </p>
        </div>
      </div>
    </template>

    <!-- Financial Architecture Drawer -->
    <template v-else-if="activeDrawer === 'financialArchitecture'">
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">Financial Architecture</h3>
        <div class="restricted-drawer-content-simple">
          <p class="restricted-message">
            This document is only available for confirmed investors under NDA.
          </p>
          <p class="smallFontSize grey-dark" style="margin-top: var(--space)">
            The Financial Architecture presents the full structural logic of
            The33Path - a vertically integrated creative and financial ecosystem
            designed for long-term expansion. It details budget models,
            compensation structures, revenue pathways, ROI logic, and how all
            business streams interconnect across the decade.
          </p>
        </div>
      </div>
    </template>

    <!-- Full Business Plan Drawer -->
    <template v-else-if="activeDrawer === 'businessPlan'">
      <div class="gated-drawer-wrapper">
        <h3 class="serif bigText text-center">Full Business Plan</h3>
        <div class="restricted-drawer-content-simple">
          <p class="restricted-message">
            This document is only available for confirmed investors under NDA.
          </p>
          <p class="smallFontSize grey-dark" style="margin-top: var(--space)">
            The Full Business Plan reveals the complete creative and operational
            architecture of The33Path: its narrative system, brand identity,
            production strategy, multi-stream model, and long-term roadmap for
            global cultural presence. It outlines how each stream generates and
            compounds value throughout the decade.
          </p>
        </div>
      </div>
    </template>
  </BottomDrawer>

  <!-- Feedback Toast for all request actions -->
  <FeedbackToast
    :message="accessRequestedMessage"
    :variant="feedbackVariant"
    :show="showFeedbackToast"
    @dismissed="dismissFeedback"
  />
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useStore } from "vuex";
import useAnimationObserver from "@/composables/useAnimationObserver";
import DynamicBackgroundWatcher from "@/components/GenericComponents/DynamicBackgroundWatcher.vue";
import BottomDrawer from "@/components/GenericComponents/BottomDrawer.vue";
import SwissStyleTable from "@/components/GenericComponents/SwissStyleTable.vue";
// import FilmProjectsRoadmap from "@/components/BusinessModelComponents/FilmProjectsRoadmap.vue";
import FilmProjectsRoadmapPath from "@/components/BusinessModelComponents/FilmProjectsRoadmapPath.vue";
import TheAppHeader from "@/components/BusinessModelComponents/TheAppHeader.vue";
import BusinessStreamContent from "@/components/BusinessModelComponents/BusinessStreamContent.vue";
import FeedbackToast from "@/components/GenericComponents/FeedbackToast.vue";
import InvestorOverviewContent from "@/components/InvestorOverview/InvestorOverviewContent.vue";
import { businessStreamsData } from "@/data/businessStreamsData";
import investorOverviewData from "@/data/investorOverviewData";

import { auth, db } from "@/firebase";
import { canAccessContent } from "@/firebase/useContent";
import { requestContentAccess } from "@/firebase/useContentRequests";
import { doc, getDoc } from "firebase/firestore";

export default {
  name: "TheBusinessModel",
  components: {
    BottomDrawer,
    SwissStyleTable,
    // FilmProjectsRoadmap,
    FilmProjectsRoadmapPath,
    DynamicBackgroundWatcher,
    TheAppHeader,
    BusinessStreamContent,
    FeedbackToast,
    InvestorOverviewContent,
  },
  setup() {
    // Initialize animation observer
    const { reobserve } = useAnimationObserver();
    // Drawer state
    const activeDrawer = ref(null);

    // Get the Vuex store for reactive userRole
    const store = useStore();

    // State for content access
    const canAccessArtefacts = ref(false);
    const canAccessApp = ref(false);
    const canAccessFilmProjects = ref(false);
    const canAccessCharacters = ref(false);
    const canAccessDocumentary = ref(false);
    const canAccessPartnerships = ref(false);
    const canAccessHelix = ref(false);
    const canAccessInvestorOverview = ref(false);

    // Use store's userRole for real-time reactivity when role changes in database
    const userRole = computed(() => store.state.userRole || "visitor");
    // Normalized role for case-insensitive comparisons (DB stores 'IC', 'CI' in uppercase)
    const normalizedRole = computed(
      () => userRole.value?.toLowerCase() || "visitor",
    );
    const isLoading = ref(true);

    // Content enabled status
    const isArtefactsEnabled = ref(true);
    const isAppEnabled = ref(true);
    const isFilmProjectsEnabled = ref(true);
    const isCharactersEnabled = ref(true);
    const isDocumentaryEnabled = ref(true);
    const isPartnershipsEnabled = ref(true);
    const isHelixEnabled = ref(true);

    // Content visibility levels (fetched from Firestore)
    const contentVisibility = ref({
      theFilmProjects: "ic",
      theCharacters: "ci",
      theArtefacts: "ic",
      theApp: "ic",
      theDocumentary: "ic",
      thePartnerships: "ic",
      theHelix: "ci",
    });

    // Request handling
    const accessRequestedMessage = ref("");
    const showFeedbackToast = ref(false);
    const feedbackVariant = ref("success");
    const isProcessing = ref(false);

    // Dismiss feedback toast
    function dismissFeedback() {
      showFeedbackToast.value = false;
      accessRequestedMessage.value = "";
    }

    // Helper function to get restriction message based on content visibility
    function getRestrictionMessage(contentId) {
      const visibility =
        contentVisibility.value[contentId]?.toLowerCase() || "ic";

      if (visibility === "ci") {
        return "This content is available to Confirmed Investors only, under NDA.";
      } else if (visibility === "ic" || visibility === "vip") {
        return "This content is available to Investor Candidates only.";
      }
      return "This content requires elevated access.";
    }

    function isInvestorCandidateVisibility(contentId) {
      const visibility =
        contentVisibility.value[contentId]?.toLowerCase() || "ic";
      return visibility === "ic" || visibility === "vip";
    }

    // Placeholder images with proper require statements to load from assets folder
    const filmImage = ref(require("@/assets/images/film_projects.jpg"));
    const charactersImage = ref(require("@/assets/images/characters.jpg"));
    const artefactsImage = ref(require("@/assets/images/artefacts.jpg"));
    const appImage = ref(require("@/assets/images/app.jpg"));
    const documentaryImage = ref(require("@/assets/images/documentary.jpg"));
    const partnershipsImage = ref(require("@/assets/images/partnerships.jpg"));
    const helixImage = ref(require("@/assets/images/helix.jpg"));

    function openDrawer(segment) {
      activeDrawer.value = segment;
      document.body.style.overflow = "hidden"; // Prevent background scrolling
      // Scroll drawer to top when navigating to a new section
      nextTick(() => {
        const drawerContainer = document.querySelector(".drawer-container");
        if (drawerContainer) {
          drawerContainer.scrollTo({ top: 0, behavior: "instant" });
        }
      });
    }

    function closeDrawer() {
      activeDrawer.value = null;
      document.body.style.overflow = "auto"; // Restore scrolling
    }

    // Check access to all content sections
    async function checkAccess() {
      isLoading.value = true;
      try {
        // userRole is now reactive from the store - no need to fetch it separately

        // Get enabled status and visibility for each content section
        const filmProjectsDoc = await getDoc(
          doc(db, "content_settings", "theFilmProjects"),
        );
        if (filmProjectsDoc.exists()) {
          const data = filmProjectsDoc.data();
          isFilmProjectsEnabled.value = data.enabled;
          contentVisibility.value.theFilmProjects = data.visibility || "ic";
        }

        const charactersDoc = await getDoc(
          doc(db, "content_settings", "theCharacters"),
        );
        if (charactersDoc.exists()) {
          const data = charactersDoc.data();
          isCharactersEnabled.value = data.enabled;
          contentVisibility.value.theCharacters = data.visibility || "ci";
        }

        const artefactsDoc = await getDoc(
          doc(db, "content_settings", "theArtefacts"),
        );
        if (artefactsDoc.exists()) {
          const data = artefactsDoc.data();
          isArtefactsEnabled.value = data.enabled;
          contentVisibility.value.theArtefacts = data.visibility || "ic";
        }

        const appDoc = await getDoc(doc(db, "content_settings", "theApp"));
        if (appDoc.exists()) {
          const data = appDoc.data();
          isAppEnabled.value = data.enabled;
          contentVisibility.value.theApp = data.visibility || "ic";
        }

        const documentaryDoc = await getDoc(
          doc(db, "content_settings", "theDocumentary"),
        );
        if (documentaryDoc.exists()) {
          const data = documentaryDoc.data();
          isDocumentaryEnabled.value = data.enabled;
          contentVisibility.value.theDocumentary = data.visibility || "ic";
        }

        const partnershipsDoc = await getDoc(
          doc(db, "content_settings", "thePartnerships"),
        );
        if (partnershipsDoc.exists()) {
          const data = partnershipsDoc.data();
          isPartnershipsEnabled.value = data.enabled;
          contentVisibility.value.thePartnerships = data.visibility || "ic";
        }

        const helixDoc = await getDoc(doc(db, "content_settings", "theHelix"));
        if (helixDoc.exists()) {
          const data = helixDoc.data();
          isHelixEnabled.value = data.enabled;
          contentVisibility.value.theHelix = data.visibility || "ci";
        }

        // Check access for each content section using current userRole from store
        await updateContentAccess();

      } catch (error) {
        // Default to no access on error
        canAccessArtefacts.value = false;
        canAccessApp.value = false;
        canAccessFilmProjects.value = false;
        canAccessCharacters.value = false;
        canAccessDocumentary.value = false;
        canAccessPartnerships.value = false;
        canAccessHelix.value = false;
        canAccessInvestorOverview.value = false;
      } finally {
        isLoading.value = false;
      }
    }

    // Update content access based on current userRole (called when role changes)
    async function updateContentAccess() {
      canAccessArtefacts.value = await canAccessContent(
        "theArtefacts",
        userRole.value,
      );
      canAccessApp.value = await canAccessContent("theApp", userRole.value);
      canAccessFilmProjects.value = await canAccessContent(
        "theFilmProjects",
        userRole.value,
      );
      canAccessCharacters.value = await canAccessContent(
        "theCharacters",
        userRole.value,
      );
      canAccessDocumentary.value = await canAccessContent(
        "theDocumentary",
        userRole.value,
      );
      canAccessPartnerships.value = await canAccessContent(
        "thePartnerships",
        userRole.value,
      );
      canAccessHelix.value = await canAccessContent("theHelix", userRole.value);
      canAccessInvestorOverview.value = await canAccessContent(
        "investorOverview",
        userRole.value,
      );

    }

    // Request Access using our new service
    async function handleContentRequest(contentId, contentTitle) {
      // Prevent double-clicks
      if (isProcessing.value) return;

      isProcessing.value = true;

      if (auth.currentUser) {
        try {
          const success = await requestContentAccess(
            contentId,
            contentTitle,
            userRole.value,
          );

          if (success) {
            accessRequestedMessage.value = "Request sent successfully.";
            feedbackVariant.value = "success";
            showFeedbackToast.value = true;
          } else {
            accessRequestedMessage.value =
              "Failed to send request. Please try again.";
            feedbackVariant.value = "error";
            showFeedbackToast.value = true;
          }
        } catch (error) {
          accessRequestedMessage.value = "An error occurred. Please try again.";
          feedbackVariant.value = "error";
          showFeedbackToast.value = true;
        } finally {
          isProcessing.value = false;
        }
      } else {
        isProcessing.value = false;
      }
    }

    // Dynamic background now handled by DynamicBackgroundWatcher component
    // Removed global initialization

    // Animation handling now in composable
    // Removed manual Intersection Observer setup
    onMounted(() => {
      checkAccess();
    });

    // Watch for drawer changes to re-run observation
    watch(activeDrawer, (newValue) => {
      if (newValue) {
        nextTick(() => {
          reobserve();
        });
      }
    });

    // Watch for userRole changes (real-time from Firestore) and update content access
    watch(userRole, async (newRole, oldRole) => {
      if (newRole !== oldRole) {
        await updateContentAccess();
      }
    });

    return {
      activeDrawer,
      filmImage,
      charactersImage,
      artefactsImage,
      appImage,
      documentaryImage,
      partnershipsImage,
      helixImage,
      openDrawer,
      closeDrawer,
      canAccessArtefacts,
      canAccessApp,
      canAccessFilmProjects,
      canAccessCharacters,
      canAccessDocumentary,
      canAccessPartnerships,
      canAccessHelix,
      isArtefactsEnabled,
      isAppEnabled,
      isFilmProjectsEnabled,
      isCharactersEnabled,
      isDocumentaryEnabled,
      isPartnershipsEnabled,
      isHelixEnabled,
      userRole,
      normalizedRole,
      handleContentRequest,
      accessRequestedMessage,
      showFeedbackToast,
      feedbackVariant,
      dismissFeedback,
      isProcessing,
      getRestrictionMessage,
      isInvestorCandidateVisibility,
      isLoading,
      businessStreamsData,
      canAccessInvestorOverview,
      investorOverviewData,
    };
  },
};
</script>

<style scoped>
.maxWidth {
  max-width: 900px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.story-section {
  margin-bottom: var(--clickableHeight);
}

.content-section {
  margin-bottom: 3rem;
}

.business-grid-intro {
  margin-top: calc(var(--doubleSpace) + var(--largeSpace));
  max-width: 70ch;
}

/* Image container using grid: 2 columns on desktop, 1 on mobile */
.image-containerRow {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: var(--doubleSpace) 0;
}

/* Center the 7th item (helix) across both columns */
.image-wrapper.centered {
  grid-column: 1 / -1;
  max-width: 50%;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 768px) {
  .image-containerRow {
    grid-template-columns: 1fr;
  }

  .image-wrapper.centered {
    grid-column: 1;
    max-width: 100%;
  }
}

.image-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  border-radius: var(--border-radius-medium);
  overflow: hidden;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.jungian-archetypes {
  max-height: 75vh;
  opacity: 0.8;
  padding: var(--space);
}

.image-container img {
  width: 100%; /* Ensures the image takes up the full container width */
  object-fit: cover;
}
.contentsContArtefacts .image-container img {
  mix-blend-mode: multiply;
}

/* Column layout for each image-wrapper so the title and subtitle appear underneath */
.image-wrapper {
  width: 100%;
  aspect-ratio: 1 / 1.3;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

.image-wrapper h3 {
  text-align: center;
}

.image-wrapper:hover {
  transform: scale(1.01);
}

/* Rounded corners on the image only */
.characterImg {
  width: 100%;
  height: auto;
  object-fit: cover;
  transition: transform 0.3s ease-in-out;
  border-radius: var(--border-radius-medium);
}

.contentsContArtefacts {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 900px;
  margin: auto;
  overflow-x: hidden;
}

/* Restricted content styling */
.restricted {
  cursor: default;
}

.restricted-content {
  width: 100%;
  aspect-ratio: 3/4;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--border-radius-medium);
  padding: var(--largeSpace);
  margin-bottom: var(--space);
}

.restricted-message {
  color: var(--text-white);
  text-align: center;
  font-size: var(--smallFontSize);
  font-style: italic;
}

/* Gated drawer wrapper - vertically centers content */
.gated-drawer-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 120px); /* Account for footer */
  padding: var(--largeSpace) 0;
}

/* Restricted drawer content */
.restricted-drawer-content {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: var(--largeSpace);
  margin-top: var(--largeSpace);
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: var(--border-radius-medium);
}

/* Simplified restricted drawer content */
.restricted-drawer-content-simple {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100px;
  padding: var(--space);
  margin-top: var(--largeSpace);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

/* Success and error messages */
.access-request-message {
  margin-top: 0.5rem;
  font-size: var(--smallFontSize);
}

.access-request-message.success {
  color: var(--green);
}

.access-request-message.error {
  color: var(--red);
}

/* Content width adjustments */
.largestMarginTop,
p,
ul,
h3,
h4,
h5 {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

/* Table captions and blocks */
.table-caption-above {
  margin-top: var(--largeSpace);
  margin-bottom: var(--space);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.8;
}

.table-caption-below {
  margin-top: var(--largeSpace);
  margin-bottom: var(--doubleSpace);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.8;
  line-height: 1.5;
}

.business-streams-preview-block,
.seed-round-block {
  padding-bottom: 0 !important;
  margin-bottom: var(--doubleSpace);
  /* background: var(--grey-darkestest);
  padding: var(--space);
  padding-top: 0px; */
  border-radius: var(--border-radius-large);
}
.seed-round-block {
  margin-top: var(--doubleSpace);
}
.business-streams-preview-block {
  margin-bottom: var(--doubleSpace);
}

.block-title {
  font-size: var(--smallerFontSize);
  font-weight: 200;
  letter-spacing: 0.2em;
  opacity: 0.3;
  text-align: center;
  padding: var(--tinySpace);
  color: var(--text-white);
  border-bottom: 0.8px dashed var(--border-light);
}

.artefact-category-number {
  background-color: var(--lazuli-400);
  color: var(--bg-main-dark);
  font-size: var(--smallFontSize);
  font-weight: bold;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: -39px;
  z-index: 11;
  border: 3px solid var(--bg-main-dark);
  padding: 3px;
}

/* Document Access Section */
.document-access-section {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  margin-top: var(--doubleSpace);
  margin-bottom: var(--doubleSpace);
}

.document-section {
  margin-bottom: calc(var(--doubleSpace) * 4);
  margin-top: calc(var(--doubleSpace) * 3);

  display: flex;
  flex-direction: column;
  align-items: center;
}

.document-section p:first-child {
  margin-top: calc(var(--space) + var(--space));
  margin-bottom: var(--space);
  text-align: center;
  width: 100%;
  max-width: 82%;
  font-size: calc(var(--smallFontSize) * 0.92);
  line-height: 1.6;
}

.document-button {
  /* Inherits global button styles from main.css */
  padding-left: var(--largeSpace);
  padding-right: var(--largeSpace);
  width: auto;
  min-width: fit-content;
}

.document-access-note {
  margin-top: var(--space);
  opacity: 0.7;
  text-align: center;
  display: inline-flex;
  align-items: flex-start;
  gap: var(--tinySpace);
}

.icon-inline {
  width: 17px;
  height: 17px;
  fill: var(--grey-dark);
  flex-shrink: 0;
}

@media (max-width: 480px) {
  /* .business-streams-preview-block,
  .seed-round-block {
    padding: var(--smallSpace);
    padding-top: 0px;
  } */

  .document-section p:first-child {
    max-width: 100%;
  }
}

/* Partnership Principles Section */
.partnership-principles-section {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  margin-top: var(--doubleSpace);
}

.partnership-principles-section .subtitle {
  font-size: var(--smallFontSize);
  opacity: 0.7;
  margin-top: calc(-1 * var(--space));
  margin-bottom: var(--largeSpace);
}

.principles-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: calc(var(--largeSpace) * 3);
  margin: var(--doubleSpace) 0;
}

.principle-item {
  padding: var(--space);
  border-left: 2px solid var(--purple-dark-tint-4);
  line-height: 1.55rem;
  font-size: 0.9rem;
}

.principle-item h4 {
  color: var(--text-white);
  margin-bottom: var(--tinySpace);
  font-size: 1rem;
}

.principle-item p {
  font-size: var(--smallFontSize);
  opacity: 0.85;
  margin: 0;
}

.closing-statement {
  margin-top: var(--largeSpace);
  margin-bottom: var(--doubleSpace);
  font-size: 1.1rem;
  opacity: 0.9;
}

@media (max-width: 768px) {
  .principles-grid {
    grid-template-columns: 1fr;
  }
}

/* Why Now Section */
.why-now-section {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  margin-top: var(--doubleSpace);
}

.why-now-divider {
  max-width: 900px;
  margin: calc(var(--doubleSpace) * 2) auto;
  opacity: 0.6;
  border-top: 1px solid var(--border-dark);
}

.animate.fadeInSubtleUp {
  transform: translateY(4px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.animate.fadeInSubtleUp.active {
  transform: translateY(0);
}

.why-now-master {
  font-size: calc(var(--bigFontSize) * 1);
  text-align: right;
}

.why-now-section .subtitle {
  font-size: var(--smallFontSize);
  opacity: 0.7;
  margin-top: calc(-1 * var(--space));
  margin-bottom: var(--largeSpace);
}
</style>
